import { Box, Button, IconButton, Tooltip, useMediaQuery } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EmployeeProfileFIlters } from '../employee-profile-management/EmployeeProfileFilters';
import { useToastContext } from '../../hooks';
import { IEmployee, IEmployeeRole } from '../../models/employee';
import { sortable } from '../../models';
import { FullTable as Table, MobileTable, MRTTable } from '../../components';
import { Edit, Upload } from '@mui/icons-material';
import { EmployeesAPI } from '../../fetch/employee';
import { Link } from 'react-router-dom';
import { UserContext } from '../../context/user';
import { IRoleType } from '../../models/roles';
import ImportDrawsModal from './include/modals/ImportDrawsModal';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { ImportAgentDrawsResult } from '../../fetch/agent-draws-and-deductions';

export const STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

const DEFAULT_FILTERS = { IsActive: true };

const Agents = () => {
  const { showToast } = useToastContext();
  const { userContext } = React.useContext(UserContext);

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [loadingEmployeeRoles] = useState(false);
  const [employeeRoles, setEmployeeRoles] = useState<IEmployeeRole[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedSort, setSelectedSort] = useState<string>('1');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>(STATUS.ACTIVE);

  // Import Draws state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSuccessResult, setImportSuccessResult] = useState<ImportAgentDrawsResult | null>(null);

  const hasImportAccess =
    (userContext?.roles?.has(IRoleType.Accounting) || userContext?.roles?.has(IRoleType.SystemAdmin)) &&
    (userContext?.featureFlags?.some(
      (flag: any) => flag?.name === 'draws-and-deductions' && flag?.hasAccess
    ));
  const [sortDirection, setSortDirection] = useState<{
    EmployeeNumber?: sortable;
    FirstName?: sortable;
    LastName?: sortable;
    Title?: sortable;
    Department?: sortable;
    Office?: sortable;
  }>({
    EmployeeNumber: 'Asc',
  });

  const [searchValue, setSearchValue] = useState<string>('');
  const [filters, setFilters] = useState<{
    IsActive?: boolean;
    Role?: string;
  }>(DEFAULT_FILTERS);
  
    // MRT state
    const [mrtSorting, setMrtSorting] = useState<any[]>([]);
    const [mrtColumnFilters, setMrtColumnFilters] = useState<any[]>([]);
    const [mrtGlobalFilter, setMrtGlobalFilter] = useState<string>('');
    const mrtSearchDebounceRef = useRef<any>(null);
  
  
    // MRT columns definition
    const mrtColumns = useMemo(() => {
      const ellipsisCell = (text: any, maxWidth: number = 200) => (
        <Tooltip title={text ?? ''} arrow>
          <Box component="span" sx={{ 
            display: 'inline-block', 
            maxWidth, 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis' 
          }}>
            {text ?? ''}
          </Box>
        </Tooltip>
      );
  
      const roleOptions = employeeRoles.map(role => role.description);
  
      return [
        {
          accessorKey: 'employeeId',
          header: 'Employee Number',
          filterVariant: 'text' as const,
          size: 150,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140),
        },
        {
          accessorKey: 'firstName',
          header: 'First Name',
          filterVariant: 'text' as const,
          size: 150,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140),
        },
        {
          accessorKey: 'lastName',
          header: 'Last Name',
          filterVariant: 'text' as const,
          size: 150,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140),
        },
        {
          accessorKey: 'title',
          header: 'Title',
          filterVariant: 'text' as const,
          size: 200,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 180),
        },
        {
          accessorKey: 'department',
          header: 'Department',
          filterVariant: 'text' as const,
          size: 150,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140),
        },
        {
          accessorKey: 'office',
          header: 'Office',
          filterVariant: 'text' as const,
          size: 150,
          Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140),
        },
        {
          accessorKey: 'roles',
          header: 'Roles',
          filterVariant: 'multi-select' as const,
          filterSelectOptions: roleOptions,
          enableSorting: false,
          size: 200,
          Cell: ({ cell }: any) => {
            const roles = cell.getValue() || [];
            const roleNames = roles
              .map((role: string | number) =>
                employeeRoles.find(empRole => empRole.value === role)?.description
              )
              .filter(Boolean)
              .join(', ');
            return ellipsisCell(roleNames, 180);
          },
        },
        {
          id: 'actions',
          header: 'Actions',
          enableColumnFilter: false,
          enableSorting: false,
          size: 70,
          Cell: ({ row }: any) => (
            <Link to={`/draws-and-deductions/employees/${row.original.employeeId}/draws`}>
            <IconButton
              size="small"
              >
              <Edit />
            </IconButton>
            </Link>
          ),
        },
      ];
    }, [employeeRoles]);

  const columns = useMemo(() => {
    return [
      {
        accessor: 'employeeId',
        Header: 'Employee Number',
        isServerSorted: selectedSort === 'EmployeeNumber',
        isServerSortedDesc: sortDirection.EmployeeNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('EmployeeNumber'),
      },
      {
        accessor: 'firstName',
        Header: 'First Name',
        isServerSorted: selectedSort === 'FirstName',
        isServerSortedDesc: sortDirection.FirstName === 'Desc',
        handleClickColumn: () => handleClickColumn('FirstName'),
      },
      {
        accessor: 'lastName',
        Header: 'Last Name',
        isServerSorted: selectedSort === 'LastName',
        isServerSortedDesc: sortDirection.LastName === 'Desc',
        handleClickColumn: () => handleClickColumn('LastName'),
      },
      {
        accessor: 'title',
        Header: 'Title',
        isServerSorted: selectedSort === 'Title',
        isServerSortedDesc: sortDirection.Title === 'Desc',
        handleClickColumn: () => handleClickColumn('Title'),
      },
      {
        accessor: 'department',
        Header: 'Department',
        isServerSorted: selectedSort === 'Department',
        isServerSortedDesc: sortDirection.Department === 'Desc',
        handleClickColumn: () => handleClickColumn('Department'),
      },
      {
        accessor: 'office',
        Header: 'Office',
        isServerSorted: selectedSort === 'Office',
        isServerSortedDesc: sortDirection.Office === 'Desc',
        handleClickColumn: () => handleClickColumn('Office'),
      },
      {
        accessor: 'roles',
        Header: 'Roles',
        sort: false,
        Cell: ({ cell: { row: { original } } }: { cell: { row: { original: any } } }) => {
          return (
            <span>
              {original.roles
                .map((role: string | number) =>
                  employeeRoles.find(empRole => empRole.value === role)?.description
                )
                .join(', ')}
            </span>
          );
        },
      },
      {
        accessor: 'actions',
        Header: '',
        sort: false,
        Cell: ({ cell: { row: { original } } }: { cell: { row: { original: any } } }) => {
          return (
            <Link to={`/draws-and-deductions/employees/${original.employeeId}/draws`}>
              <IconButton>
                <Edit />
              </IconButton>
            </Link>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeRoles, page, perPage, selectedSort, sortDirection, filters, searchValue]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection(prev => ({
      ...prev,
      // @ts-ignore
      [column]: prev[column] === 'Asc' ? 'Desc' : 'Asc',
    }));
  };
  
    // Build MRT query params for server-side sorting and filtering
    const buildQueryParams = useCallback(() => {
      // Build server-side sort param from MRT sorting state
      const allowedSortIds = new Set([
        'employeeId',
        'firstName', 
        'lastName',
        'title',
        'department',
        'office'
      ]);
      
      const sortString = (Array.isArray(mrtSorting) ? mrtSorting : [])
        .map((s: any) => {
          const id = s.id || s.columnId || s.accessorKey;
          if (!id || !allowedSortIds.has(id)) return null;
          const dir = s.desc ? 'desc' : 'asc';
          return `${id}:${dir}`;
        })
        .filter(Boolean)
        .join(',');
  
      // Map column filters to backend parameters
      const filterMap: Record<string, any> = {};
      (Array.isArray(mrtColumnFilters) ? mrtColumnFilters : []).forEach((f: any) => {
        const id = f.id || f.column;
        const val = f.value;
        if (!id) return;
        
        if (id === 'roles' && Array.isArray(val) && val.length > 0) {
          // Handle role filter - convert role descriptions back to IDs
          const roleIds = val.map(roleName => {
            const role = employeeRoles.find(r => r.description === roleName);
            return role?.value;
          }).filter(Boolean);
          if (roleIds.length > 0) {
            filterMap.Role = roleIds[0]; // Backend only supports single role filter
          }
        }
      });
  
      return {
        page: page + 1,
        perPage,
        Name: mrtGlobalFilter || searchValue,
        IsActive: selectedStatus === STATUS.ACTIVE,
        Role: selectedRole || filterMap.Role,
        sort: sortString || undefined,
      };
    }, [mrtSorting, mrtColumnFilters, mrtGlobalFilter, searchValue, selectedStatus, selectedRole, employeeRoles, page, perPage]);
  
    // Wrapped fetchEmployees in useCallback to prevent unnecessary rere-renders
    const fetchEmployees = useCallback(async () => {
      setLoadingEmployees(true);
      try {
        const params = buildQueryParams();
        const response = await EmployeesAPI.fetchListOfEmployees(params) as { records?: IEmployee[]; totalRecordCount?: number } | IEmployee[];
  
        if ('records' in response && 'totalRecordCount' in response) {
          setEmployees(response.records || []);
          setRecordCount(response.totalRecordCount || 0);
        } else {
          setEmployees(Array.isArray(response) ? response : []);
          setRecordCount(Array.isArray(response) ? response.length : 0);
        }
      } catch (error: any) {
        showToast('error', error?.detail || 'Error trying to retrieve Employees. Please try again.');
      } finally {
        setLoadingEmployees(false);
      }
    }, [buildQueryParams, showToast]);

  
  const fetchEmployeeRoles = useCallback(async () => {
    try {
      const roles = await EmployeesAPI.fetchRoles();
      return roles; 
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Roles. Please try again.'
      );
      return [];
    }
  }, [showToast]);

  const fetchSubsidiaries = useCallback(async () => {
    try {
      const subsidiaries = await EmployeesAPI.fetchSubsidiaries();
      return subsidiaries;
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Susidiaries. Please try again.'
      );
      return [];
    }
  }, [showToast]);

  const fetchOffices = useCallback(async () => {
    try {
      const offices = await EmployeesAPI.fetchOffices();
      return offices;
    } catch (error: any) {
      showToast('error', error?.detail || 'Error trying to retrieve Offices. Please try again.');
      return [];
    }
  }, [showToast]);
  
    // Initial data loads (run once)
    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          const [
            roles, 
        
          ] = await Promise.all([
            fetchEmployeeRoles(),
            fetchSubsidiaries(),
            fetchOffices(),
          ]);
  
          setEmployeeRoles(roles);
        } catch (error) {
         // console.error("Error fetching initial data", error);
        }
      };
  
      fetchInitialData();
    }, [
      fetchEmployeeRoles,
      fetchSubsidiaries,
      fetchOffices,
    ]);
  
    
    useEffect(() => {
      fetchEmployees();
      window.scrollTo(0, 0);
    }, [fetchEmployees]);

  return (
      <Box sx={{ 
        backgroundColor: '#f0f0f0', 
        p: 2, 
        borderRadius: 2, 
        minHeight: '100%' 
      }}>
        <Box
          sx={{
            '& #roleLabel.MuiInputLabel-outlined, & #statusLabel.MuiInputLabel-outlined': {
              backgroundColor: '#f0f0f0',
              px: 0,
            },
          }}
        >
          <EmployeeProfileFIlters
            isLoading={loadingEmployees || loadingEmployeeRoles}
            applyFilters={(clearFilters?: boolean) => {
              if (clearFilters) {
                setFilters(DEFAULT_FILTERS);
                setSearchValue('');
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  Role: selectedRole || undefined,
                  IsActive: selectedStatus === STATUS.ACTIVE ? true : false,
                });
              }
            }}
            roles={employeeRoles}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchValue(val);
            }}
            hasAppliedFilters={hasAppliedFilters}
            setHasAppliedFilters={setHasAppliedFilters}
            statuses={[
              { value: STATUS.ACTIVE, description: 'Active', shorthand: 'Active' },
              { value: STATUS.INACTIVE, description: 'Inactive', shorthand: 'Inactive' },
            ]}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            extraActions={hasImportAccess ? (
              <Button
                color="secondary"
                variant="contained"
                startIcon={<Upload />}
                onClick={() => setIsImportModalOpen(true)}
                sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap', mt: '4px', flexShrink: 1, minWidth: 0 }}
              >
                Import Draws
              </Button>
            ) : undefined}
          />
        </Box>
        <Box sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          background: 'white',
          overflow: 'auto',
          '& table': {
            width: '100%',
            tableLayout: 'auto',
          },
          '& th:first-of-type, & td:first-of-type': {
            paddingLeft: '8px !important',
            textAlign: 'left !important',
          },
          '& .MuiTableCell-root': {
            fontSize: '0.75rem',
            padding: '6px 8px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            wordBreak: 'keep-all',
            overflow: 'hidden',
            maxWidth: '150px',
            borderBottom: '1px solid rgb(212, 210, 210)',
          },
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            maxWidth: '150px',
            transition: 'color 0.3s ease',
            borderBottom: '2px solid rgb(212, 210, 210)',
          },
          '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
            color: '#64b5f6',
            cursor: 'pointer',
          },
          '& th:last-of-type, & td:last-of-type': {
            position: 'sticky',
            right: 0,
            zIndex: 2,
            textAlign: 'center',
            minWidth: '80px !important',
            width: '100px !important',
            maxWidth: '100px !important',
          },
          'td:last-of-type': {
            p: 0,
            backgroundColor: '#fff',
          },
          '& .MuiTableCell-body': {
            py: 0.1,
          },
          '&::-webkit-scrollbar': {
            height: 2,
          },
          '&::-webkit-scrollbar-track': {
            background: '#e0e0e0',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#a0a0a0',
            borderRadius: 3,
          },
          scrollbarHeight: 'thin',
          scrollbarColor: ' #fff  #fff',
        }}>
          {isMobile ? (
            <Table
              columns={columns}
              data={employees}
              isLoading={loadingEmployees || loadingEmployeeRoles}
              serverPage={page}
              serverRecordCount={recordCount}
              serverPerPage={perPage}
              handlePage={setPage}
              handleRowsPerPage={setRowsPerPage}
              ResponsiveComponent={MobileTable}
              mobileProps={{
                fields: columns
                  .filter(col => col.Header)
                  .map(col => ({
                    name: col.Header,
                    accessor: col.accessor,
                    Cell: col?.Cell,
                  })),
                mobileAccordionLabel: (original: any) =>
                  `${original?.firstName ?? ''} ${original?.lastName ?? ''}`,
              }}
            />
          ) : (
            <MRTTable
              columns={mrtColumns as any}
              data={employees as any}
              loading={loadingEmployees || loadingEmployeeRoles}
              manualPagination
              manualSorting
              totalRowCount={recordCount}
              pageIndex={page}
              pageSize={perPage}
              onPaginationChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
                if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
                if (typeof next?.pageSize === 'number') setRowsPerPage(next.pageSize);
              }}
              onSortingChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtSorting) : updater;
                setMrtSorting(next || []);
                setPage(0); // Reset to first page when sorting changes
              }}
              sorting={mrtSorting}
              columnFilters={mrtColumnFilters}
              onColumnFiltersChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtColumnFilters) : updater;
                setMrtColumnFilters(next || []);
                setPage(0); // Reset to first page when filters change
              }}
              globalFilter={mrtGlobalFilter}
              onGlobalFilterChange={(val: any) => {
                const v = typeof val === 'string' ? val : '';
                setMrtGlobalFilter(v);
                // Debounce the search
                if (mrtSearchDebounceRef.current) {
                  clearTimeout(mrtSearchDebounceRef.current);
                }
                mrtSearchDebounceRef.current = setTimeout(() => {
                  if (v.trim().length >= 2 || v.trim().length === 0) {
                    setPage(0);
                    setSearchValue(v.trim());
                  }
                }, 300);
              }}
            />
          )}
        </Box>
        <ImportDrawsModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={(result: ImportAgentDrawsResult) => {
            setImportSuccessResult(result);
          }}
        />
        {importSuccessResult && (
          <ConfirmationDialogue
            open
            id="import-draws-success"
            title="Import Successful"
            text={`Successfully imported ${importSuccessResult.successCount} draw(s). Approval emails are being sent in the background.`}
            hideCancel
            onClose={() => setImportSuccessResult(null)}
            onConfirm={() => setImportSuccessResult(null)}
          />
        )}
    </Box>
  )
}

export default Agents;